import * as L from 'leaflet';
import '@maplibre/maplibre-gl-leaflet';
import 'leaflet.markercluster';
import 'leaflet-extra-markers';
import $ from 'jquery';

interface CustomMarker extends L.Marker {
    hotelId: string;
    hotelUrl: string;
}

interface DefaultMapSettings {
    latitude: number;
    longitude: number;
    zoom: number;
}

declare global {
    interface Window {
        _defaultMapSettings: DefaultMapSettings;
    }
}

interface HotelData {
    providerID: string;
    providerStarRanking: number;
    providerLatitude?: number;
    providerLongitude?: number;
    maplocation?: MapLocation,
    latitude?: number;
    longitude?: number;
    providerMediaImageName: string;
    providerName: string;
    reviewRating: number;
    oneNightPrice?: number;
    currencyIsSymbolAfterValue?: boolean;
    currencySymbol?: string;
    currency?: {
        symbol: string;
        isSymbolAfterValue: boolean;
    };
}

interface MapLocation {
    name: string;
    location: Location,
    latitude?: number;
    longitude?: number;
}

interface Location {
    name: string;
}

interface HotelInfo {
    id: string;
    name: string;
    starRanking: number;
    reviewRating: number;
    latitude?: number;
    longitude?: number;
    maplocation?: MapLocation,
}

interface HotelDealData {
    hotelInfo: HotelInfo;
    imageUrl?: string;
    discountBadgeText?: string;
    oldPrice?: string;
    discountPrice?: string;
    dealDescription?: string;
}

type ReviewScore = 1 | 2 | 3 | 4 | 5;

type ScreenMessages = typeof window._screenMessages;

type ReviewLabelKey = keyof Pick<
    ScreenMessages,
    'ReviewLabel1' | 'ReviewLabel2' | 'ReviewLabel3' | 'ReviewLabel4' | 'ReviewLabel5'
    >;

class MapHelperClass {
    private mainmap?: L.Map;
    private mainmarkersraw: CustomMarker[] = [];
    private mainmarkers: L.FeatureGroup = L.featureGroup();
    
    constructor() {
        $(document).on('click', '.db-list-location', (e) => {
            const hotelId = $(e.currentTarget).data('hotel-id');
            this.selectProviderOnMap(hotelId);
        });
    }

    public addServiceWorkers(): void {
        if ('serviceWorker' in navigator) {
            window.addEventListener('load', () => {
                navigator.serviceWorker.register('mapServiceWorker.js').catch((err) => {
                    console.error('ServiceWorker registration failed:', err);
                });
            });
        }
    }

    public initMainMap(id: string): void {
        try {
            if (!this.mainmap) {
                const settings = window._defaultMapSettings;

                this.mainmap = L.map(id, {
                    center: [settings.latitude, settings.longitude],
                    zoom: settings.zoom,
                    maxZoom: 16,
                    minZoom: 6,
                    zoomSnap: 1,
                    zoomDelta: 1,
                    zoomAnimation: false,       // 🚀 big win for vector layers
                    fadeAnimation: false,       // avoids opacity fades
                    markerZoomAnimation: false, // reduces DOM work
                });

                // Add your layers
                this.mainmap.addLayer(this.getMapTilerAndLibreVectorAttributeLayer());

                // ✅ Create a marker cluster group instead of a plain LayerGroup
                this.mainmarkers = L.markerClusterGroup({
                    showCoverageOnHover: false,
                    disableClusteringAtZoom: 15, // stop clustering when zoomed in close
                    spiderfyOnEveryZoom: false,
                    maxClusterRadius: 50,        // smaller = tighter clusters
                    iconCreateFunction: (cluster) => {
                        const count = cluster.getChildCount();
                        return L.divIcon({
                            html: `<div class="cluster-badge">${count}</div>`,
                            className: 'custom-cluster',
                            iconSize: [40, 40],
                        });
                    }
                });

                this.mainmap.addLayer(this.mainmarkers);

                // Close popups when clicking the map background
                this.mainmap.on('click', (e: L.LeafletMouseEvent) => {
                    const popup = document.getElementById('marker-popup');

                    // Safely get the DOM target (fallbacks in case originalEvent is missing)
                    const domTarget = (e && (e as any).originalEvent?.target) ?? (e as unknown as Event).target;

                    // If the popup exists and the click was inside it, do nothing
                    if (popup && domTarget && popup.contains(domTarget as Node)) {
                        return;
                    }

                    // Hide custom popup
                    if (popup) {
                        popup.classList.remove('show');
                    }

                    // Unselect any selected markers
                    this.mainmarkersraw.forEach((m) => {
                        m.getElement()?.classList.remove('selected');
                        m.setZIndexOffset(0);
                    });
                });

                this.mapRefresh();
            }
        } catch (error) {
            console.error(error);
            console.log("The map is not supported by your browser :-(");
        }
    }

    public mapRefresh(): void {
        if (this.mainmap) {
            this.mainmap.invalidateSize();
        }
    }

    public toggleMapControls(isEnabled: boolean): void {
        console.log(isEnabled);

        if (this.mainmap) {
            if (isEnabled) {
                this.mainmap.dragging.enable();
                this.mainmap.scrollWheelZoom.enable();
                this.mainmap.doubleClickZoom.enable();
                this.mainmap.touchZoom.enable();
                this.mainmap.boxZoom.enable();
                this.mainmap.keyboard.enable();
            } else {
                this.mainmap.dragging.disable();
                this.mainmap.scrollWheelZoom.disable();
                this.mainmap.doubleClickZoom.disable();
                this.mainmap.touchZoom.disable();
                this.mainmap.boxZoom.disable();
                this.mainmap.keyboard.disable();
            }
        }
    }

    public mapRefreshWithBounds(): void {
        if (this.mainmap) {
            this.mainmap.invalidateSize();
            const bounds = this.mainmarkers.getBounds();
            if (bounds.isValid()) this.mainmap.fitBounds(bounds);
        }
    }

    public selectProviderOnMap(hotelId: string) {
        let marker: L.Marker | undefined = undefined;

        for (let i = 0; i < this.mainmarkersraw.length; i++) {
            // assuming you store hotelId on marker.options as string or number
            if (hotelId.toString() === (this.mainmarkersraw[i] as any).hotelId?.toString()) {
                marker = this.mainmarkersraw[i];
                break; // found it, stop loop early
            }
        }

        if (!marker) {
            console.warn('Marker for hotelId', hotelId, 'not found');
            return;
        }

        setTimeout(() => {
            this.mainmap!.setView(marker.getLatLng(), 16);
        }, 1000);

        setTimeout(() => {
            marker.fire('click');
        }, 1000);
    }

    public toReviewScore(input?: number | null): string {
        let value = input ?? 1;

        if (value < 1) value = 1;
        if (value > 5) value = 5;

        return (Math.round(value * 2 * 10) / 10).toLocaleString('en');
    }

    public toReviewScoreInt(input: number): ReviewScore {
        let value = input;

        if (value < 1) value = 1;
        if (value > 5) value = 5;

        return Math.ceil(value) as ReviewScore;
    }

    public getReviewScoreLabel(rating: number): string {
        const score = this.toReviewScoreInt(rating);
        const key = `ReviewLabel${score}` as const;

        return window._screenMessages[key];
    }

    public addMarkerToMain(hotelData: HotelData, url: string, colour: string, tenant: string, price?: number, isFavourite?: boolean): void {
        const soldOut = colour === 'unavailable';
        const showPrice = price !== undefined || hotelData.oneNightPrice !== undefined;
        let priceString = '';

        const favouriteButton = isFavourite
            ? `<button class="btn-remove-favourite" aria-label="Remove from favourites" data-provider-id="${hotelData.providerID}">
                 <i class="icon-heart-filled"></i>
               </button>`
                    : `<button class="btn-add-favourite" aria-label="Add to favourites" data-provider-id="${hotelData.providerID}">
                 <i class="icon-heart"></i>
               </button>`;

        if (soldOut) {
            priceString = window._screenMessages.SoldOut;
        } else if (showPrice) {
            const currencyIsSymbolAfterValue = hotelData.currencyIsSymbolAfterValue ?? hotelData.currency?.isSymbolAfterValue;
            let symbol = hotelData.currencySymbol ?? hotelData.currency?.symbol ?? '';
            if (price != undefined) {
                symbol = '';
            }

            if (price != undefined) {
                priceString = currencyIsSymbolAfterValue
                    ? `${price} ${symbol}`
                    : `${symbol}${price}`;
            } else {
                priceString = currencyIsSymbolAfterValue
                    ? `${hotelData.oneNightPrice} ${symbol}`
                    : `${symbol}${hotelData.oneNightPrice?.toFixed(2)}`;
            }
        }

        const lat = hotelData.providerLatitude ?? hotelData.latitude;
        const lng = hotelData.providerLongitude ?? hotelData.longitude;

        if (lat !== undefined && lng !== undefined) {
            const marker = L.marker([lat, lng]) as CustomMarker;
            marker.hotelId = hotelData.providerID;
            marker.hotelUrl = url;
            marker.setIcon(L.divIcon({
                className: 'custom-price-marker',
                html: `<div class='price-badge ${colour}'>${priceString}</div>`,
                iconSize: [80, 30],
                iconAnchor: [30, 15]
            }));
            marker.on('click', () => {
                this.mainmarkersraw.forEach((m) => m.getElement()?.classList.remove('selected'));
                this.mainmarkersraw.forEach((m) => m.setZIndexOffset(0));
                marker.getElement()?.classList.add('selected');
                marker.setZIndexOffset(1000);

                // Populate marker-popup
                const popup = document.getElementById('marker-popup') as HTMLElement;
                const content = popup.querySelector('.popup-content') as HTMLElement;

                content.innerHTML = `
                  <div class="hotel-list-card map-infobox flex-container" data-hotel-url="${marker.hotelUrl}">
                    <div class="hotel-image">
                      <div class="main-image">
                        <img class="infoimg link-to-hotel" src="${hotelData.providerMediaImageName}" 
                             onerror="Global.imgError(this, '${tenant}');" alt="Main Image" />
                        ${favouriteButton}
                      </div>
                    </div>

                    <div class="hotel-info">
                      <h2 class="hotel-name link-to-hotel">
                        ${hotelData.providerName}
                        <span class="rating-stars">
                          ${'<span class="fa-solid fa-star"></span>'.repeat(hotelData.providerStarRanking || 0)}
                        </span>
                      </h2>

                      <div class="hotel-location link-to-hotel">
                        <i class="fa fa-location-dot"></i>
                        <span>
                          ${hotelData.maplocation?.name}, ${hotelData.maplocation?.location?.name} &#x2022;

                          ${hotelData.maplocation?.latitude != null && hotelData.maplocation?.longitude != null
                            ? this.calculateDistanceInKmWithLabel(lat, lng, hotelData.maplocation.latitude, hotelData.maplocation.longitude) + " from centre"
                            : ""}
                        </span>
                      </div>

                      ${hotelData.reviewRating ? `
                        <div class="card-review-score review-open link-to-hotel" data-provider-id="${hotelData.providerID}">
                          <span class="score">${this.toReviewScore(hotelData.reviewRating)}</span>
                          <span class="text"><strong>${this.getReviewScoreLabel(hotelData.reviewRating)}</strong></span>
                        </div>
                      ` : ''}

                      <div class="price-desc link-to-hotel">
                        <div class="price">
                          <span class="amount">${priceString}</span><span class="per-night">&nbsp;/&nbsp;per night</span>
                        </div>
                      </div>
                    </div>
                  </div>
                `;

                // Handle any clickable `.link-to-hotel` elements in the popup
                const hotelCard = content.querySelector('.hotel-list-card') as HTMLElement;
                const hotelUrl = hotelCard?.getAttribute('data-hotel-url');

                if (hotelUrl) {
                    content.querySelectorAll('.link-to-hotel').forEach(link => {
                        link.addEventListener('click', (e) => {
                            e.stopPropagation();

                            if (window.innerWidth > 992) {
                                e.preventDefault();
                                window.open(hotelUrl, '_blank');
                            } else {
                                // same tab on mobile / small screens
                                window.location.href = hotelUrl;
                            }
                        });
                    });
                }

                popup.classList.add('show');
            });
            marker.on('mouseover', () => {
                marker.setZIndexOffset(1000);
            });

            marker.on('mouseout', () => {
                marker.setZIndexOffset(0);
            });

            /*const popupHTML = `
        <div class="inner map-infobox flex-container">
          <div class="img-popup-map">
            <img class="infoimg" src="${hotelData.providerMediaImageName}" onerror="Global.imgError(this, '${tenant}');" />
          </div>
          <div class="info-text-container">
            <div class="info-text">
              <span class="infobox-title">${hotelData.providerName}</span>
              <div class="infobox-price">
                ${soldOut
                    ? "Sold Out"
                    : `From ${priceString} per night`}
              </div>
              <a href="javascript:MapHelper.jumpToProvider('${hotelData.providerID}');" class="db-button db-button-cta">
                ${soldOut ? "Check other dates" : "Rooms and Prices"}
              </a>
            </div>
          </div>
        </div>`;

            marker.bindPopup(popupHTML, { maxWidth: 500, className: 'infoBox' });*/

            this.mainmarkers.addLayer(marker);
            this.mainmarkersraw.push(marker);
        }
    }

    public addDealMarkerToMain(hotelDealData: HotelDealData, url: string, colour: string, tenant: string, isFavourite?: boolean): void {
        let priceString = '';

        const favouriteButton = isFavourite
            ? `<button class="btn-remove-favourite" aria-label="Remove from favourites" data-provider-id="${hotelDealData.hotelInfo.id}">
                 <i class="icon-heart-filled"></i>
               </button>`
            : `<button class="btn-add-favourite" aria-label="Add to favourites" data-provider-id="${hotelDealData.hotelInfo.id}">
                 <i class="icon-heart"></i>
               </button>`;

        const lat = hotelDealData.hotelInfo.latitude;
        const lng = hotelDealData.hotelInfo.longitude;

        if (lat !== undefined && lng !== undefined) {
            const marker = L.marker([lat, lng]) as CustomMarker;
            marker.hotelId = hotelDealData.hotelInfo.id;
            marker.hotelUrl = url;
            marker.setIcon(L.divIcon({
                className: 'custom-price-marker',
                html: `<div class='price-badge ${colour}'>${hotelDealData.discountPrice}</div>`,
                iconSize: [80, 30],
                iconAnchor: [30, 15]
            }));
            marker.on('click', () => {
                this.mainmarkersraw.forEach((m) => m.getElement()?.classList.remove('selected'));
                this.mainmarkersraw.forEach((m) => m.setZIndexOffset(0));
                marker.getElement()?.classList.add('selected');
                marker.setZIndexOffset(1000);

                hotelDealData.hotelInfo.reviewRating = 10;
                hotelDealData.hotelInfo.starRanking = 5;

                // Populate marker-popup
                const popup = document.getElementById('marker-popup') as HTMLElement;
                const content = popup.querySelector('.popup-content') as HTMLElement;
                content.innerHTML = `
                  <div class="hotel-list-card map-infobox flex-container" data-hotel-url="${marker.hotelUrl}">
                    <div class="hotel-image">
                      <div class="main-image deal-image">
                        <img class="infoimg link-to-hotel" src="${hotelDealData.imageUrl}" alt="Main Image" />
                        ${favouriteButton}
                      </div>
                    </div>

                    <div class="deal-hotel-info w-100">
                      <h2 class="hotel-name link-to-hotel">
                        ${hotelDealData.hotelInfo.name}
                      </h2>

                      <div class="location link-to-hotel">
                        <i class="fa fa-map-marker-alt"></i><span>${hotelDealData.hotelInfo.maplocation?.name}, ${hotelDealData.hotelInfo.maplocation?.location?.name}</span>
                      </div>

                      ${hotelDealData.hotelInfo.reviewRating ? `
                        <div class="card-review-score review-open link-to-hotel" data-provider-id="${hotelDealData.hotelInfo.id}">
                          <span class="score">${hotelDealData.hotelInfo.reviewRating}</span>
                          <span class="text"><strong>Excellent</strong></span>
                        </div>
                      ` : ''}

                      ${hotelDealData.dealDescription ? `
                        <div class="desc">
                          ${hotelDealData.dealDescription}
                        </div>
                      ` : ''}

                      <div class="deal-price-info">
                        ${hotelDealData.discountBadgeText ? `<span class="discount-badge link-to-hotel">${hotelDealData.discountBadgeText}</span>` : ''}
                        <div class="deal-price-wrapper">
                            ${hotelDealData.oldPrice ? `<span class="old-price link-to-hotel">${hotelDealData.oldPrice}</span>` : ''}
                            ${hotelDealData.discountPrice ? `<span class="discount-price link-to-hotel">${hotelDealData.discountPrice}</span>` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                `;

                // Handle any clickable `.link-to-hotel` elements in the popup
                const hotelCard = content.querySelector('.hotel-list-card') as HTMLElement;
                const hotelUrl = hotelCard?.getAttribute('data-hotel-url');

                if (hotelUrl) {
                    content.querySelectorAll('.link-to-hotel').forEach(link => {
                        link.addEventListener('click', (e) => {
                            e.stopPropagation();

                            if (window.innerWidth > 992) {
                                e.preventDefault();
                                window.open(hotelUrl, '_blank');
                            } else {
                                // same tab on mobile / small screens
                                window.location.href = hotelUrl;
                            }
                        });
                    });
                }

                popup.classList.add('show');
            });
            marker.on('mouseover', () => {
                marker.setZIndexOffset(1000);
            });

            marker.on('mouseout', () => {
                marker.setZIndexOffset(0);
            });

            /*const popupHTML = `
        <div class="inner map-infobox flex-container">
          <div class="img-popup-map">
            <img class="infoimg" src="${hotelData.providerMediaImageName}" onerror="Global.imgError(this, '${tenant}');" />
          </div>
          <div class="info-text-container">
            <div class="info-text">
              <span class="infobox-title">${hotelData.providerName}</span>
              <div class="infobox-price">
                ${soldOut
                    ? "Sold Out"
                    : `From ${priceString} per night`}
              </div>
              <a href="javascript:MapHelper.jumpToProvider('${hotelData.providerID}');" class="db-button db-button-cta">
                ${soldOut ? "Check other dates" : "Rooms and Prices"}
              </a>
            </div>
          </div>
        </div>`;

            marker.bindPopup(popupHTML, { maxWidth: 500, className: 'infoBox' });*/

            this.mainmarkers.addLayer(marker);
            this.mainmarkersraw.push(marker);
        }
    }

    public permHideMarker(hotelId: string): void {
        this.mainmarkersraw = this.mainmarkersraw.filter((m) => {
            if (m.hotelId === hotelId) {
                this.mainmarkers.removeLayer(m);
                return false;
            }
            return true;
        });
    }

    public hideMarker(hotelId: string): void {
        this.mainmarkersraw.forEach((m) => {
            if (m.hotelId === hotelId) {
                (m.options as any).hidden = true;
            }
        });
    }

    public showMarker(hotelId: string): void {
        this.mainmarkersraw.forEach((m) => {
            if (m.hotelId === hotelId) {
                delete (m.options as any).hidden;
            }
        });
    }

    public addMainMarkersLayer(): void {
        if (!this.mainmap) return;

        // Remove old cluster layer if it exists
        if (this.mainmarkers) {
            this.mainmap.removeLayer(this.mainmarkers);
        }

        // Create a new cluster group
        this.mainmarkers = L.markerClusterGroup({
            showCoverageOnHover: false,
            disableClusteringAtZoom: 15,
            spiderfyOnEveryZoom: false,
            maxClusterRadius: 50,
            iconCreateFunction: (cluster) => {
                const count = cluster.getChildCount();
                // Determine cluster color/class dynamically if you want
                return L.divIcon({
                    html: `<div class="cluster-badge">${count}</div>`,
                    className: 'custom-cluster',
                    iconSize: [40, 40],
                    iconAnchor: [20, 20]
                });
            }
        });

        // Add only visible markers
        for (const m of this.mainmarkersraw) {
            if (!(m.options as any).hidden) {
                this.mainmarkers.addLayer(m);
            }
        }

        // Add to map
        this.mainmap.addLayer(this.mainmarkers);
    }

    public removeMainMarkersLayer(): void {
        this.mainmap?.removeLayer(this.mainmarkers);
    }

    public highlightMarker(hotelId: string) {
        const marker = this.mainmarkersraw.find(m => m.hotelId === hotelId);
        if (marker) {
            marker.setZIndexOffset(1000);
            marker.getElement()?.classList.add("hover-highlight");
        }
    }

    public unhighlightMarker(hotelId: string) {
        const marker = this.mainmarkersraw.find(m => m.hotelId === hotelId);
        if (marker) {
            marker.setZIndexOffset(0);
            marker.getElement()?.classList.remove("hover-highlight");
        }
    }

    public jumpToProvider(providerId: string): void {
        const openProviderPage = () => {
            const anchor = $(`#hotelList .db-card[data-hotel-id='${providerId}'] a`).first();
            if (anchor.length > 0) {
                const url = anchor.attr('href');
                if (url) {
                    window.innerWidth > 992 ? window.open(url, '_blank') : (window.location.href = url);
                }
            }
        };

        const checkContentLoaded = () => {
            if ($(`#hotelList .db-card[data-hotel-id='${providerId}'] a`).length > 0) {
                openProviderPage();
            } else {
                const observer = new MutationObserver((mutations) => {
                    for (const mutation of mutations) {
                        if (mutation.addedNodes.length > 0) {
                            openProviderPage();
                            observer.disconnect();
                            break;
                        }
                    }
                });

                const targetNode = document.getElementById('hotelList');
                if (targetNode) {
                    observer.observe(targetNode, { childList: true, subtree: true });
                }
            }
        };

        setTimeout(checkContentLoaded, 200);
    }

    private getMapTilerAndLibreVectorAttributeLayer(): L.Layer {
        return L.maplibreGL({
            style: "https://digibreaksprodstorage.blob.core.windows.net/map-styles/map_mobile_style.json",
        });
    }

    private calculateDistanceInKmNoLabel(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const R = 6371; // Earth radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;

        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * Math.PI / 180) *
            Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) ** 2;

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c;

        if (d > 1) {
            return Math.round(d * 10) / 10; // 1 decimal place
        } else if (d < 0.5) {
            return Math.round(d * 1000); // metres if < 0.5km
        }

        return d;
    }

    private calculateDistanceInKmWithLabel(lat1: number, lon1: number, lat2: number, lon2: number): string {
        const d = this.calculateDistanceInKmNoLabel(lat1, lon1, lat2, lon2);

        if (d > 0.999) {
            return `${d}km`;
        } else {
            return `${Math.round(d * 1000)}m`;
        }
    }
}

// Create global instance
export const MapHelper = new MapHelperClass();
(window as any).MapHelper = MapHelper;